import { PractitionersSearchResult } from '~/api';
import { SearchResultGroupByLocation } from '~/utilities/types';

/**
 * Groups search results by clinic location and applies a small offset to the location coordinates to avoid overlapping markers
 * Also randomizes the practitioners within each clinic group for fresh searches
 * and only randomizes the new clinic groups for pagination
 * to maintain consistency of result ordering throughout paginated searches
 * @param rawResults Array of raw practitioner search results
 * @param existingGroupedResults Existing grouped results for pagination (optional)
 * @param isLoadMore Whether this is a pagination request (default: false)
 * @returns Processed grouped results with coordinate offsets applied
 */
export const groupSearchResultsByLocation = (
  rawResults: PractitionersSearchResult[],
  isLoadMore: boolean,
  existingGroupedResults?: SearchResultGroupByLocation[]
): SearchResultGroupByLocation[] => {
  if (!rawResults) return [];

  const positionCounts = new Map<string, number>();
  let hasDuplicatePosition = false;

  // Start with existing groups for pagination, empty array for fresh search
  const initialGroups =
    isLoadMore && existingGroupedResults ? [...existingGroupedResults] : [];

  const groupedResults = rawResults.reduce<SearchResultGroupByLocation[]>(
    (acc, result) => {
      const existingIndex = acc.findIndex(
        (item) => item.clinicLocationGuid === result.clinicLocationGuid
      );

      if (existingIndex !== -1) {
        acc[existingIndex] = {
          ...acc[existingIndex],
          practitioners: [...acc[existingIndex].practitioners, result],
        };
        return acc;
      }

      const newResult = {
        clinicLocationGuid: result.clinicLocationGuid,
        locationCoordinates: result.locationCoordinates,
        clinicName: result.clinicName,
        practitioners: [result],
        streetAddress: result.streetAddress,
        streetAddress2: result.streetAddress2,
        city: result.city,
        province: result.province,
        phone: result.clinicPhone,
        bookingUrl: result.clinicBookingUrl,
      };

      // Check for duplicate coordinates while building the array, if there are duplicates, we will apply a small deterministic offset
      // so markers are not layered on top of each other
      const posKey = `${result.locationCoordinates.latitude},${result.locationCoordinates.longitude}`;
      const count = positionCounts.get(posKey) || 0;
      positionCounts.set(posKey, count + 1);
      if (count > 0) {
        hasDuplicatePosition = true;
      }

      acc.push(newResult);
      return acc;
    },
    initialGroups
  );

  // Randomize practitioners within clinic groups
  if (!isLoadMore) {
    // Fresh search: randomize all groups
    groupedResults.forEach((group) => {
      group.practitioners.sort(() => Math.random() - 0.5);
    });
  } else if (existingGroupedResults) {
    // Pagination: only randomize NEW clinic groups that weren't in existing results
    const existingClinicIds = new Set(
      existingGroupedResults.map((group) => group.clinicLocationGuid)
    );

    groupedResults.forEach((group) => {
      // Only randomize if this clinic wasn't in the previous results
      if (!existingClinicIds.has(group.clinicLocationGuid)) {
        group.practitioners.sort(() => Math.random() - 0.5);
      }
    });
  }

  // If no duplicate marker positions, return as-is to avoid unnecessary processing
  if (!hasDuplicatePosition) {
    return groupedResults;
  }

  // Reset the map for the actual offset calculation
  positionCounts.clear();

  return groupedResults.map((result) => {
    const posKey = `${result.locationCoordinates.latitude},${result.locationCoordinates.longitude}`;
    const currentCount = positionCounts.get(posKey) || 0;
    positionCounts.set(posKey, currentCount + 1);

    // If result has a duplicate position, apply a small offset
    if (currentCount > 0) {
      const offsetLat =
        result.locationCoordinates.latitude + (Math.random() - 0.5) / 2500;
      const offsetLng =
        result.locationCoordinates.longitude + (Math.random() - 0.5) / 2500;

      return {
        ...result,
        locationCoordinates: {
          latitude: offsetLat,
          longitude: offsetLng,
        },
      };
    }

    return result;
  });
};
